<template>
  <div class="gs-app gm-forms-catalog" style="height: calc(100vh - 65px)">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <div class="flex mx-4 pb-10 gm-forms-catalog__shell">
      <div class="w-full gm-forms-catalog__workspace">
        <!-- Can mail/Upgraded user/Sandbox mode check -->
        <div
          class="w-full btn-radius bg-white text-grey-700 p-4 mb-2"
          style="box-shadow: 10px 5px 10px 0 rgba(22, 29, 37, 0.05)"
          v-if="showSenderBanner && this.userCanMail"
          :style="{
            display: hideBanner ? 'none' : 'block',
          }"
        >
          <create-sender-banner
            @closeSenderBanner="closeSenderBanner"
            @smsSenderCreated="closeSenderBanner"
          ></create-sender-banner>
        </div>
        <!-- Can mail/Upgraded user/Sandbox mode check -->

        <navigation-tabs
          v-if="showNavTabs"
          class="gm-nav-forms-tabs"
          :class="{
            'gm-nav-forms-tabs-no-sender': showSenderBanner && !hideBanner,
          }"
          :nav_tabs="navTabs"
        ></navigation-tabs>

        <server-table
          class="text-center groovemail-form-table gs-refresh-controls"
          url="/forms"
          ref="serverTable"
          :tableRef="'formsTable'"
          :slotData="slotData"
          :tableColumns="mail_listsTableColumns"
          :tableOptions="mail_listsTableOptions"
          :tableTitle="''"
          :addNewBtnText="'frontend_groovemail_forms_index_new_form'"
          :emptyStateData="emptyStateData"
          @addNewData="openCreateForm"
          @loaded="hideLoader()"
          @loading="showLoader()"
        >
        </server-table>

        <gs-named-modal class="gs-refresh-controls"
          name="create-form-modal"
          :title="$t('frontend_groovemail_forms_create_new_form')"
        >
          <div class="p-6">
            <div class="flex flex-col mb-6">
              <label class="font-semibold text-sm text-grey-700 mb-2">
                {{ $t('frontend_groovemail_admin_forms_form_name') }}
              </label>
              <text-input
                variant="base"
                v-model="model.name"
                :placeholder="$t('frontend_groovemail_adminsetting_name')"
                :validator="$v.model.name"
              />
            </div>

            <div class="flex justify-end">
              <gd-button
                size="auto"
                @click="closeCreateForm"
                >{{ $t('frontend_cancel') }}</gd-button
              >
              <gd-button
                class="ml-2"
                size="auto"
                variant="primary"
                @click="SaveForm"
              >
                {{ $t('frontend_groovemail_forms_save_and_continue') }}
              </gd-button>
            </div>
          </div>
        </gs-named-modal>

        <gs-named-modal class="gs-refresh-controls"
          name="edit-form-modal"
          :title="$t('frontend_groovemail_forms_rename_form_name')"
        >
          <div class="p-6">
            <div class="flex flex-col mb-6">
              <label class="font-semibold text-sm text-grey-700 mb-2">
                {{ $t('frontend_groovemail_admin_forms_form_name') }}
              </label>
              <text-input
                variant="base"
                v-model="model.name"
                :placeholder="$t('frontend_groovemail_adminsetting_name')"
                :validator="$v.model.name"
              />
            </div>

            <div class="flex justify-end">
              <gd-button
                size="auto"
                @click="closeRenameFormModal"
                >{{ $t('frontend_cancel') }}</gd-button
              >
              <gd-button
                class="ml-2"
                size="auto"
                variant="primary"
                @click="renameFormName"
              >
                {{ $t('frontend_groovemember_builder_rename') }}
              </gd-button>
            </div>
          </div>
        </gs-named-modal>

        <gs-named-modal class="gs-refresh-controls"
          id="embedcodemodel"
          name="embed-code-modal"
          :title="$t('frontend_product_embed_code')"
        >
          <div class="p-6">
            <div class="flex flex-wrap" v-if="!showCode">
              <div class="w-full">
              <gd-dropdown
                class="mt-2 mb-6 text-sm"
                v-model="activePresetId"
                :options="presetsSelect"
                max-height="200px"
                :placeholder="$t('frontend_groovemail_forms_select_a_preset')"
                size="full"
              />

              </div>
              <div class="w-1/2 flex justify-center">
                <checkout-radio
                  class="checkout-tab cursor-pointer html-raw-selector"
                  v-model="trackingFlow"
                  :option-name="
                    $t('frontend_groovemail_forms_step6_rawhtml')
                  "
                  option-icon="CodeIcon"
                ></checkout-radio>
              </div>
              <div class="w-1/2 flex justify-center relative js-tab-wrapper">
                <i class="js-icon fab fa-node-js" @click="switchToJs()"></i>
                <checkout-radio
                  class="checkout-tab js-tab cursor-pointer"
                  v-model="trackingFlow"
                  :option-name="
                    $t('frontend_groovemail_forms_step6_the_js')
                  "
                  option-icon="CodeIcon"
                ></checkout-radio>
              </div>
            </div>
            <div v-if="showCode">
              <div>
                <p class="text-sm font-semibold mb-2 text-copy">
                  {{ $t('frontend_groovemail_forms_index_paste_this') }}
                </p>
                <text-area
                  variant="base"
                  style="height: 80px"
                  v-model="htmlcode"
                  readonly
                />
              </div>
              <div class="flex justify-center w-full text-center mt-2">
                <gd-button
                  v-clipboard:copy="htmlcode"
                  v-clipboard:success="onCopyEmbedCode"
                  v-clipboard:error="onErrorEmbedCode"
                  variant="primary"
                  size="auto"
                  class="h-10 btn-width px-4 btn-radius text-base font-semibold"
                >
                  {{ $t('frontend_groovemail_forms_index_copy') }}
                </gd-button>
              </div>
            </div>

            <div id="formhtml" style="display: none" v-if="presets">
              <html-raw-form class-prefix="groovemail-form-raw-html-embed-" :active-preset="activePreset" :mailform="mailform" />
            </div>

            <div class="flex justify-end mt-6">
              <gd-button
                size="auto"
                @click="goBack()"
              >
                {{ $t('frontend_groovemail_forms_create_back') }}
              </gd-button>
              <gd-button
                variant="primary"
                class="ml-2"
                size="auto"
                v-if="!showCode"
                @click="goNext()"
              >
                {{ $t('frontend_groovemail_forms_index_next') }}
              </gd-button>
            </div>
          </div>
        </gs-named-modal>

        <gs-named-modal class="gs-refresh-controls"
          id="clone-form-modal"
          name="clone-form-modal"
          :title="$t('frontend_groovemail_forms_clone_form_modal_title')"
        >
          <add-name
            :name="cloneFormName"
            nameFieldHeaderText="frontend_groovemail_admin_forms_form_name"
            actionButtonText="frontend_groovemail_forms_clone_form_modal_title"
            validationAttributeText="frontend_name"
            placeholderText="frontend_name"
            @closeModal="closeCloneFormModal"
            @executeAction="fetchFormData"
          ></add-name>
        </gs-named-modal>


        <confirm-modal
          :ref="`confirmation-archive-form`"
          :name="`confirmation-archive-form`"
          :buttonText="$t('frontend_confirm')"
          :cancelText="$t('frontend_cancel')"
          :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
          :messageContent="this.$t('frontend_groovemail_swal_revert_message')"
          @confirm="archiveform"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Treeselect from '@riophae/vue-treeselect';
import Notify from '@groovepages/gd-ui-kit/notify';
import { mapGetters, mapState } from 'vuex';

import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import themeConfig from '@/theme/themeConfig.js';

import Mailform from '@/models/Mailform';

import CreateSenderBanner from '@/components/createSenderBanner.vue';
import AddName from '@/components/add-name.vue';
import NavigationTabs from '@/components/navigation-tabs.vue';
import showAlert from '@/components/sweet-alert-popup.js';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import HtmlRawForm from '../../components/HtmlRawForm.vue';

export default {
  name: 'Forms',
  middleware: 'auth',
  layout: 'defaultWithoutVuePerfectScrollbar',
  metaInfo() {
    return { title: this.$t('frontend_forms') };
  },
  data() {
    return {
      showNavTabs: true,
      showCode: false,
      hideBanner: false,
      showSenderBanner: false,
      senders: [],
      wizardColor: themeConfig.appPrimaryColor,
      fullPageLoader: true,
      isLoading: false,
      formIdToClone: null,
      cloneFormName: '',
      targetFormId: '',
      navTabs: [
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_forms',
          tabRoute: 'Forms',
        },
        {
          tabName:
            'frontend_groovemail_components_pricepoint_sidebar_customfields',
          tabRoute: 'CustomField',
        },
      ],
      embedUrl: '',
      parmalink: '',
      htmlcode: '',
      mailform: [],
      trackingFlow: 0,
      codeoption: '',
      model: {
        name: '',
        optin: 'single',
        confirmlink_sendoption: 'email',
        confirmlink_page: 'custom',
        confirmlink_content: `${this.$t(
          'frontend_forms_confirmation_default_content'
        )}: {optin_confirmation_link}`,
        confirmlink_sms_content: `${this.$t(
          'frontend_forms_confirmation_default_content'
        )}: {optin_confirmation_link}`,
        fetched_form_fields: [],
        form_fields: [
          {
            type: 'textbox',
            placeholder: 'Email',
            id: 'standard1',
            identifier: 'Email',
            mandatory: 1,
            name: 'email',
            fieldtype: 'standard',
          },
          {
            type: 'textbox',
            placeholder: 'First Name',
            id: 'standard2',
            identifier: 'Firstname',
            mandatory: 0,
            name: 'first_name',
            fieldtype: 'standard',
          },
        ],
        welcome_msg_option: 'notsend',
      },
      loaderColor: themeConfig.appPrimaryColor,
      mail_listsTableColumns: ['name', 'total_leads', 'actions'],
      mail_listsTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['name', 'total_leads'],
        headings: {
          name: () => this.$t('frontend_groovemail_forms_name_header'),
          total_leads: () =>
            this.$t('frontend_groovemail_forms_submissions_header'),
          actions: () => this.$t('frontend_groovemail_forms_index_actions'),
        },
        filterable: ['name'],
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
        pagination: { chunk: 5 },
        requestFunction(data) {
          const self = this;
          const requestParams = { ...data };
          if (!(requestParams && requestParams.orderBy)) {
            requestParams.orderBy = 'created_at';
            requestParams.ascending = 0;
          }
          return axios
            .get(self.url, {
              params: requestParams,
            })
            .catch((e) => {
              self.dispatch('error', e);
            });
        },
        responseAdapter({ data }) {
          return {
            data: data.data,
            count: data.total,
          };
        },
      },
      emptyStateData: {
        emptyStateHeading: 'frontend_forms',
        emptyStateSubHeading: 'frontend_groovemail_forms_have_no_data',
        emptyStateBtnText: 'frontend_groovemail_forms_index_new_form',
      },
    };
  },

  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
    },
  },

  components: {
    Treeselect,
    CreateSenderBanner,
    NavigationTabs,
    AddName,
    HtmlRawForm,
    ServerTable,
  },

  computed: {
    ...mapGetters({
      activePreset: 'forms/activePreset',
    }),
    ...mapState({
      presets: state => state.forms.presets,
    }),
    activePresetId: {
      get() { return this.$store.state.forms.activePresetId; },
      async set(value) {
        await this.$store.dispatch('forms/setActivePreset', value);
        // this.addcode(this.showCodeLink ? 'javascript' : 'html');
      }
    },
    presetsSelect() {
      // get the list of presets that are not a local preset
      const presetsList = this.presets.filter(preset => preset.form_id === null || preset.form_id === undefined);

      // get the target form local preset if it exists
      const targetPreset = this.presets.find(preset => preset.form_id === this.targetFormId);

      if(targetPreset) {
        // Add a new value to the top of the array
        presetsList.unshift(targetPreset);
      }

      return presetsList.map((variant) => {
        return { value: variant.id, text: variant.name };
      });
    },
    options() {
      return [
        {
          id: 'html',
          title: this.$t('frontend_groovemail_forms_step6_rawhtml'),
          description: this.$t(
            'frontend_groovemail_forms_raw_html_description'
          ),
        },
        {
          id: 'javascript',
          title: this.$t('frontend_groovemail_forms_step6_the_js'),
          description: this.$t(
            'frontend_groovemail_forms_javascript_description'
          ),
        },
      ];
    },

    mainTabClass() {
      if (this.currentTab === 'step1') {
        return 'pb-4 gs-default-tabs-item';
      }
      return 'pb-4 gs-default-tabs-item';
    },

    //  Can mail/Upgraded user/Sandbox mode check
    userCanMail() {
      return this.getCanMail();
    },
    //  Can mail/Upgraded user/Sandbox mode check

    slotData() {
      return [
        {
          slotName: 'name',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.name,
        },
        {
          slotName: 'total_leads',
          slotType: 'clickable',
          classes: () => '',
          value: (props) => props.row.total_leads ? props.row.total_leads : 0,
          callback: (props) => this.gotToFilterActiveFilled(props.row),
          defaultValue: 0,
          
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => true,
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t('frontend_groovemail_forms_index_preview'),
              conditions: () => true,
              callback: (props)=> this.ShowPreviewModel(props.row.id),
            },
            {
              id: 1,
              name: () => this.$t('frontend_groovemail_forms_index_edit'),
              conditions: () => true,
              callback: (props) => this.startUpdatingForm(props.row.id),
            },
            {
              id: 2,
              name: () => this.$t('frontend_clone'),
              conditions: () => true,
              callback: (props) => this.intiateCloneForm(props.row),
            },
            {
              id: 3,
              name: () => this.$t('frontend_groovemail_forms_index_getcode'),
              conditions: () => true,
              callback: (props) => this.GetCode(props.row.id),
            },
            {
              id: 4,
              name: () => this.$t('frontend_groovemember_builder_rename'),
              conditions: () => true,
              callback: (props) => this.openRenameFormModal(props.row.id),
            },
            {
              id: 5,
              name: () => this.$t('frontend_groovemail_forms_index_archive'),
              conditions: () => true,
              callback: (props) => this.openArchiveFormConfirm(props.row.id),
            },
          ],
        },
      ];
    },
  },

  async created() {
    this.fetchSenderStatus();
    this.$store.dispatch('forms/loadPresets');

    // embed url = input: url/groovemail/
    this.embedUrl = window.config.grooveappsurl + this.$store.getters.supBrand.url;
  },

  methods: {
    //  Can mail/Upgraded user/Sandbox mode check
    ...mapGetters('mailing', ['getCanMail']),
    //  Can mail/Upgraded user/Sandbox mode check

    goBack() {
      if (this.showCode) {
        this.showCode = !this.showCode;
      } else {
        this.$root.$emit('modal-close', 'embed-code-modal');
      }
    },

    goNext() {
      this.showCode = !this.showCode;
      this.addcode(this.trackingFlow);
    },

    onCopyEmbedCode() {
      Notify.success(
        this.$t('frontend_groovemail_forms_embed_code_copy_success')
      );
    },

    onErrorEmbedCode() {
      Notify.fail(this.$t('frontend_groovemail_forms_embed_code_copy_error'));
    },

    async openRenameFormModal(formid) {
      this.showLoader();

      try {
        this.mailform = await Mailform.$find(formid);
        this.model.name = this.mailform.name;
        this.$root.$emit('modal-open', 'edit-form-modal');
        
        this.hideLoader();
      } catch (error) {
        // show backend error alert
        this.backendErrorAlert(error.response?.data?.error);
      }
    },

    closeRenameFormModal() {
      this.$root.$emit('modal-close', 'edit-form-modal');
    },


    ShowPreviewModel(formid){
      this.$router.push({ name: 'PreviewForm', params: { id: formid } });
    },

    // open archive form modal
    async openArchiveFormConfirm(formId) {
      // set the form id
      this.targetFormId = formId;

      // open the modal
      this.$refs[`confirmation-archive-form`].open();
    },

    // close delete category modal
    closeArchiveFormConfirm() {
      // reset the category id
      this.targetFormId = '';

      // close the modal
      this.$refs[`confirmation-archive-form`].close();
    },

    // archive form
    async archiveform() {
      // show loader
      this.showLoader();

      try {
        // archive form request
        await axios.post('/archiveform', {form_id: this.targetFormId});
  
        // refresh forms table
        await this.fetchForms();

        // close the modal
        this.closeArchiveFormConfirm();
      } catch (error) {
        // show backend error alert
        this.backendErrorAlert(error.response?.data?.error);
      }
    },

    async switchToJs() {
      this.trackingFlow = this.$t('frontend_groovemail_forms_step6_the_js');
    },

    async GetCode(formid) {
      this.showLoader();
      this.targetFormId = formid;
      this.showCode = false;

      try {
        this.mailform = await Mailform.$find(formid);
        if (this.mailform.default_preset && this.mailform !== '') {
          this.activePresetId = this.mailform.default_preset;
        } else {
          this.activePresetId = this.presets[0].id;
        }
        let { lists } = this.mailform;
        if (typeof lists === 'string') {
          lists = JSON.parse(lists);
        }
        if (lists && lists.length) {
          this.parmalink = window.btoa(formid);
          this.$root.$emit('modal-open', 'embed-code-modal');
          this.trackingFlow = this.$t('frontend_groovemail_forms_step6_rawhtml');
          this.telephoneInputId = `telephone_input${this.mailform.id}`;
          this.model.fetched_form_fields = JSON.parse(this.mailform.form_fields);
        } else {
          Notify.fail(
            this.$t('frontend_groovemail_forms_list_not_configured_for_form')
          );
        }

        // hide loader
        this.hideLoader();

      } catch (error) {
        // show backend error alert
        this.backendErrorAlert(error.response?.data?.error);
      }
    },

    closecode() {
      this.$root.$emit('modal-close', 'embed-code-modal');
      this.htmlcode = '';
      this.mailform = '';
    },

    openCreateForm() {
      this.model.name = '';
      this.$root.$emit('modal-open', 'create-form-modal');
    },
    closeCreateForm() {
      this.$root.$emit('modal-close', 'create-form-modal');
    },
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    closeSenderBanner() {
      this.hideBanner = true;
    },

    gotToFilterActiveFilled(filled) {
      console.log(filled);
      this.$router.push({
        name: 'Leads2',
        query: {
          formSubmissionsId: filled.id,
          formSubmissionsName: filled.name,
        }
      });
    },

    async fetchSenderStatus() {
      try {
        const sendersData = await axios.get('/email-sms-sender');
        if (sendersData.data.admin_setting && !sendersData.data.account_senders) {
          this.showSenderBanner = true;
        } else {
          this.showSenderBanner = false;
        }

      } catch (error) {
        // show backend error alert
        this.backendErrorAlert(error.response?.data?.error);
      }
    },

    async SaveForm() {
      this.$v.model.name.$touch();
      if (!this.$v.model.name.$invalid) {
        this.showLoader();

        this.mailform = new Mailform({});
        this.mailform.name = this.model.name;
        this.mailform.tags = JSON.stringify([]);
        this.mailform.lists = JSON.stringify([]);
        this.mailform.sequences = JSON.stringify([]);
        this.mailform.form_fields = JSON.stringify(this.model.form_fields);
        this.mailform.confirmlink_sendoption = this.model.confirmlink_sendoption;

        // set the confirmation email content based on the selected option
        if(this.model.confirmlink_sendoption === 'email'){
          this.mailform.confirmlink_content = this.model.confirmlink_content;
        } else if(this.model.confirmlink_sendoption === 'sms'){
          this.mailform.confirmlink_content = this.model.confirmlink_sms_content;
        } else {
          this.mailform.confirmlink_content = '';
        }

        this.mailform.optin = this.model.optin;
        this.mailform.confirmlink_page = this.model.confirmlink_page;
        this.mailform.welcome_msg_option = this.model.welcome_msg_option;

        try {
          const response = await this.mailform.save();
          this.showNavTabs = false;
          this.$router.push({ name: 'NewForm', params: { id: response.id } });
          
          // hide loader
          this.hideLoader();
        } catch (error) {
          // show backend error alert
          this.backendErrorAlert(error.response?.data?.error);
        }
      }
    },

    // rename form name
    async renameFormName() {
      this.$v.model.name.$touch();

      if (!this.$v.model.name.$invalid) {
        this.showLoader();
        this.mailform.name = this.model.name;

        try {
          await this.mailform.save();
          await this.fetchForms();
          this.$root.$emit('modal-close', 'edit-form-modal');
        } catch (error) {
          // show backend error alert
          this.backendErrorAlert(error.response?.data?.error);
        }
      }
    },

    startUpdatingForm(formid) {
      this.showNavTabs = false;
      this.$router.push({ name: 'EditForm', params: { id: formid } });
    },

    addcode(option) {
      if (option === this.$t('frontend_groovemail_forms_step6_rawhtml')) {
        const formhtml = document.querySelector('#formhtml');
        this.htmlcode = `${formhtml.firstChild.innerHTML}<link rel="stylesheet" type="text/css" href="${this.embedUrl}embed/app.css"/><script src="${this.embedUrl}embed/countryCodeInput.js" formId="${this.mailform.id}"><\/script>`;
      } else if (option === this.$t('frontend_groovemail_forms_step6_the_js')) {
        this.htmlcode = '';
        const variation = this.activePresetId !== undefined ? `variation="${this.activePresetId}"` : '';
        const iframecode = `<groovemail-form-embed permalink="${this.parmalink}" ${variation}></groovemail-form-embed>`;
        this.htmlcode = `${iframecode}<script src="${this.embedUrl}embed/app.js"><\/script>`;
      }
    },

    openCloneFormModal() {
      this.$root.$emit('modal-open', 'clone-form-modal');
    },

    closeCloneFormModal() {
      this.$root.$emit('modal-close', 'clone-form-modal');
    },

    intiateCloneForm(form) {
      this.formIdToClone = form.id;
      this.cloneFormName = `${form.name} [ ${this.$t(
        'frontend_copy'
      ).toUpperCase()} ]`;
      this.openCloneFormModal();
    },

    async fetchFormData(newFormName) {
      try {
        this.showLoader();
        const formToClone = await Mailform.$find(this.formIdToClone);
        let newConfiramtionEmailId = null;
        if (formToClone.confirm_email_id) {
          newConfiramtionEmailId = await axios.put(
            `/email/template/clone/${formToClone.confirm_email_id}`
          );
        }
        let newWelcomeEmailId = null;
        if (formToClone.welcome_email_id) {
          newWelcomeEmailId = await axios.put(
            `/email/template/clone/${formToClone.welcome_email_id}`
          );
        }
        await this.prepareFormData(
          formToClone,
          newFormName,
          newConfiramtionEmailId?.data?.id,
          newWelcomeEmailId?.data?.id
        );

      } catch (error) {
        // show backend error alert
        this.backendErrorAlert(error.response?.data?.error);
      }
    },

    async prepareFormData(
      formToClone,
      name,
      confirmEmailId = null,
      welcomeEmailId = null
    ) {
      const formData = new Mailform({});
      formData.name = name;
      formData.form_fields = formToClone.form_fields;
      if (formToClone.recipients) {
        formData.recipients = formToClone.recipients;
      }
      formData.default_acc_email = formToClone.default_acc_email;
      formData.optin = formToClone.optin;
      if (formToClone.form_heading) {
        formData.form_heading = formToClone.form_heading;
      }
      if (formToClone.form_sub_heading) {
        formData.form_sub_heading = formToClone.form_sub_heading;
      }
      formData.form_submit_button_text =
        formToClone.form_submit_button_text ||
        this.model.form_submit_button_text;
      formData.confirmlink_sendoption = formToClone.confirmlink_sendoption;
      formData.confirmlink_gateway = formToClone.confirmlink_gateway;
      formData.confirmlink_sender_id =
        formToClone.confirmlink_sender_id || null;
      if (formToClone.confirmlink_subject) {
        formData.confirmlink_subject = formToClone.confirmlink_subject;
      }
      if (formToClone.confirmlink_previewtext) {
        formData.confirmlink_previewtext = formToClone.confirmlink_previewtext;
      }
      if (formData.confirmlink_sendoption === 'email') {
        formData.confirm_editor_type = formToClone.confirm_editor_type || 1;
        if (confirmEmailId) {
          formData.confirm_email_id = confirmEmailId;
          formData.confirmlink_content = confirmEmailId;
          formData.confirm_template_id = confirmEmailId;
        } else {
          formData.confirmlink_content = formToClone.confirmlink_content;
          formData.confirm_email_sections =
            formToClone.confirm_email_sections || '';
        }
      } else if (formData.confirmlink_sendoption === 'sms') {
        formData.confirmlink_content = formToClone.confirmlink_content;
      }
      formData.confirmlink_page = formToClone.confirmlink_page;
      formData.confirmlink_destination = formToClone.confirmlink_destination;
      formData.confirmlink_hypertext = formToClone.confirmlink_hypertext;
      formData.confirmlink_pageurl = formToClone.confirmlink_pageurl;
      formData.success_hypertext = formToClone.success_hypertext;
      formData.success_destination = formToClone.success_destination;
      formData.success_option = formToClone.success_option || 'url';
      formData.success_pageurl =
        formToClone.success_pageurl || this.$store.getters.appUrl;
      formData.welcome_msg_option = formToClone.welcome_msg_option;
      formData.welcome_msg_gateway = formToClone.welcome_msg_gateway;
      formData.welcome_sender_id = formToClone.welcome_sender_id;
      if (formToClone.welcome_msg_subject) {
        formData.welcome_msg_subject = formToClone.welcome_msg_subject;
      }
      if (formToClone.welcome_msg_previewtext) {
        formData.welcome_msg_previewtext = formToClone.welcome_msg_previewtext;
      }
      if (formData.welcome_msg_option === 'email') {
        formData.welcome_editor_type = formToClone.welcome_editor_type || 3;
        if (welcomeEmailId) {
          formData.welcome_email_id = welcomeEmailId;
          formData.welcome_msg_content = welcomeEmailId;
          formData.welcome_template_id = welcomeEmailId;
        } else {
          formData.welcome_msg_content = formToClone.welcome_msg_content;
          formData.welcome_email_sections =
            formToClone.welcome_email_sections || '';
        }
      } else if (formToClone.welcome_msg_option === 'sms') {
        formData.welcome_msg_content = formToClone.welcome_msg_content;
      }
      formData.tags = formToClone.tags;
      formData.lists = formToClone.lists;
      formData.sequences = formToClone.sequences;
      formData.form_design = formToClone.form_design;
      await this.cloneForm(formData);
    },

    async cloneForm(formData) {
      try {
        this.showLoader();
        
        await formData.save();
        this.closeCloneFormModal();
        await this.fetchForms();
      } catch (error) {
        // show backend error alert
        this.backendErrorAlert(error.response?.data?.error);
      }
    },

    async fetchForms() {
      await this.$refs.serverTable.$refs.formsTable.refresh();
    },


    // backend error alert
    async backendErrorAlert() {
      // hide loader
      this.hideLoader();

      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark"> ${ this.$t('frontend_groovemail_forms_view_unknown_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },
  },
};
</script>

<style lang="scss">
.html-raw-selector {
  .icon {
    width: 100%!important;
    text-align: center!important;
    display: flex!important;
    justify-content: center!important;
    margin-right: 0px!important;
    margin-bottom: 10px!important;
    left: auto!important;
    top: auto!important;
  }
}
.groovemail-form-table {
  .VueTables--server table {
    th:nth-child(1),
    td:nth-child(1),
    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(3),
    td:nth-child(3) {
      width: 28%;
    }
  }
  .VueTables__table th:focus,
  td:focus {
    outline: 0px;
  }
}

.text-copy {
  color: #545671;
}

.checkout-tab {
  div:first-child {
    margin: 0px auto;
    position: relative;
    padding: 40px 0px;
    
    &:focus {
      outline: none;
    }
    
    .icon {
      position: absolute;
      top: 0px;
      margin: 0px;
    }
    
    .icon svg {
      stroke: black;
    }
  }
}


.js-tab div:first-child .icon svg {
  width: 0px;
}

.js-tab-wrapper .js-icon {
  position: absolute;
  z-index: 2;
  pointer-events: none;
  font-size: 75px;
  top: 35px;
  cursor: pointer;
}

.gs-default-tabs-item:focus {
  outline: none !important;
  outline-style: none !important;
}

#embedcodemodel .shadow-2xl.relative.overflow-hidden {
  width: 45% !important;
}

.gm-nav-forms-tabs {
  margin-bottom: 20px;
  row-gap: 8px;
}


// ------------ custom checked ----------------- //
.form-builder-wrapper,
.form-wrapper {
  /* The container */
  .container {
    /* Hide the browser's default checkbox */
    input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
    }

    /* Style the checkmark/indicator */
    .checkmark:after {
      left: 9px;
      top: 5px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 3px 3px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }

  /* Create a custom checkbox */
  .checkmark {
    position: absolute;
    top: 0;
    left: 0;
    height: 24px;
    width: 24px;
    background-color: #fff;
    border: solid 1px #d5d8e5;
    border-radius: 4px;

    /* Create the checkmark/indicator (hidden when not checked) */
    &:after {
      content: '';
      position: absolute;
      display: none;
    }
  }

  /* On mouse-over, add a grey background color */
  .container:hover input ~ .checkmark {
    background-color: #fff;
  }

  /* Show the checkmark when checked */
  .container input:checked ~ .checkmark:after {
    display: block;
  }

  // ----------------------------- //
  .radio-container {
    /* Hide the browser's default radio button */
    input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
    }

    /* Style the indicator (dot/circle) */
    .radio-checkmark:after {
      top: 3px;
      left: 3px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
    }
  }

  /* Create a custom radio button */
  .radio-checkmark {
    position: absolute;
    top: 0;
    left: 0;
    height: 24px;
    width: 24px;
    background-color: #fff;
    border: solid 1px #d5d8e5;
    border-radius: 50%;

    /* Create the indicator (the dot/circle - hidden when not checked) */
    &:after {
      content: '';
      position: absolute;
      display: none;
    }
  }

  /* On mouse-over, add a grey background color */
  .radio-container:hover input ~ .radio-checkmark {
    background-color: #fff;
    border: solid 1px #d5d8e5;
  }

  /* When the radio button is checked, add a variant="primary" background */
  .radio-container input:checked ~ .radio-checkmark {
    background-color: #fff;
  }

  /* Show the indicator (dot/circle) when checked */
  .radio-container input:checked ~ .radio-checkmark:after {
    display: block;
  }
}
</style>

<style scoped>
.gm-forms-catalog {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-forms-catalog__shell {
  margin: 0;
}

.gm-forms-catalog__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-forms-catalog >>> .VueTables__table,
.gm-forms-catalog >>> .VueTables__table th,
.gm-forms-catalog >>> .VueTables__table td {
  border-color: var(--gs-border);
}

@media (max-width: 640px) {
  .gm-forms-catalog { padding: 18px 12px 32px; }
  .gm-forms-catalog__workspace { padding: 16px; }
}
</style>
